/**
 * persistence/managers/ReviewerVersionManager.js
 *
 * Local-only version history for the Reviewer Portal.
 *
 * The reviewer's working state is the `eventStrings` array — a list of
 * { value, status, comments, error, isFixed } items. Unlike the analyst VCS
 * (Yjs commits keyed by analystId), reviewer state isn't a token string, so it
 * gets its own lightweight IndexedDB store. Snapshots are never synced to the
 * cloud — they exist purely so a reviewer can roll back their session locally.
 *
 * API: saveReviewerSnapshot, listReviewerSnapshots, getReviewerSnapshot,
 *      deleteReviewerSnapshot.
 */

import { v4 as uuidv4 } from "uuid";
import { reviewerVersionsTable } from "../db/database";
import { REVIEWER_VERSIONS_MAX_PER_SESSION } from "../config";

/** Strip transient UI-only fields before persisting. */
function sanitize(eventStrings) {
  return (eventStrings ?? []).map((s) => ({
    value: s.value,
    status: s.status === "current" ? "upcoming" : s.status, // don't freeze a cursor
    comments: s.comments ?? "",
    error: s.error ?? null,
    isFixed: !!s.isFixed,
  }));
}

function summarize(items) {
  const total = items.length;
  const reviewed = items.filter((s) => s.status === "reviewed").length;
  const errors = items.filter((s) => s.status === "error").length;
  return { total, reviewed, errors, pending: total - reviewed - errors };
}

/**
 * Append a snapshot of the reviewer's current state, then prune to the cap.
 *
 * @param {object} p
 * @param {string|number} p.matchId
 * @param {string|number} p.reviewerId  logged-in reviewer id
 * @param {Array} p.eventStrings        current review list
 * @param {string} [p.label]            human label for the entry
 * @returns {Promise<number|null>} new row id, or null on failure/no-op
 */
export async function saveReviewerSnapshot({ matchId, reviewerId, eventStrings, label }) {
  if (!matchId || !reviewerId || !eventStrings?.length) return null;
  try {
    const items = sanitize(eventStrings);
    const record = {
      snapshotId: uuidv4(),
      matchId: String(matchId),
      reviewerId: String(reviewerId),
      createdAt: Date.now(),
      label: label || "Auto snapshot",
      summary: summarize(items),
      eventStrings: items,
    };
    const id = await reviewerVersionsTable().add(record);
    await prune(matchId, reviewerId);
    return id;
  } catch (err) {
    console.warn("[ReviewerVersionManager] save failed:", err.message);
    return null;
  }
}


/** Newest-first list of snapshots for a session. */
export async function listReviewerSnapshots({ matchId, reviewerId }) {
  if (!matchId || !reviewerId) return [];
  try {
    const rows = await reviewerVersionsTable()
      .where("[matchId+reviewerId]")
      .equals([String(matchId), String(reviewerId)])
      .sortBy("createdAt");
    return rows.reverse();
  } catch (err) {
    console.warn("[ReviewerVersionManager] list failed:", err.message);
    return [];
  }
}

/** Fetch one snapshot by row id. */
export async function getReviewerSnapshot(id) {
  try {
    return await reviewerVersionsTable().get(id);
  } catch {
    return null;
  }
}

/** Delete one snapshot by row id. */
export async function deleteReviewerSnapshot(id) {
  try {
    await reviewerVersionsTable().delete(id);
  } catch (err) {
    console.warn("[ReviewerVersionManager] delete failed:", err.message);
  }
}

/** Keep only the most recent REVIEWER_VERSIONS_MAX_PER_SESSION rows. */
async function prune(matchId, reviewerId) {
  try {
    const rows = await reviewerVersionsTable()
      .where("[matchId+reviewerId]")
      .equals([String(matchId), String(reviewerId)])
      .sortBy("createdAt");
    const excess = rows.length - REVIEWER_VERSIONS_MAX_PER_SESSION;
    if (excess > 0) {
      const old = rows.slice(0, excess).map((r) => r.id);
      await reviewerVersionsTable().bulkDelete(old);
    }
  } catch (err) {
    console.warn("[ReviewerVersionManager] prune failed:", err.message);
  }
}
